import { type ReactNode, useEffect, useId, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, X } from "lucide-react";

/**
 * Full-page modal layout for immersive detail views (walkthroughs, reports).
 * Replaces the normal Page wrapper — covers the entire viewport with its own
 * header, scrollable content area, and close button.
 *
 * Set `stickyHeader` to keep the full header pinned (default for backwards compat).
 * When false, only the close button stays sticky and the title scrolls away.
 */
export function FullPageModal({
  title,
  subtitle,
  actions,
  onClose,
  stickyHeader = true,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  onClose?: () => void;
  stickyHeader?: boolean;
  children: ReactNode;
}) {
  const titleId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus management: focus on mount, restore focus on unmount
  useEffect(() => {
    const previousActiveElement = document.activeElement as HTMLElement | null;
    const container = containerRef.current;
    if (container) {
      const focusable = container.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length > 0) {
        focusable[0].focus({ preventScroll: true });
      } else {
        container.focus({ preventScroll: true });
      }
    }

    return () => {
      previousActiveElement?.focus();
    };
  }, []);

  // Focus trap: keep Tab within the modal
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // A panel opened on top (Radix sheet/dialog) handles its own Escape
      // and marks the event — close only that, not the whole modal.
      if (e.key === "Escape" && onClose && !e.defaultPrevented) {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = container.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      className="fixed inset-0 z-50 bg-background outline-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="h-full overflow-y-auto bg-gradient-to-b from-background via-background to-primary/[0.03]">
        {stickyHeader ? (
          /* Sticky Header */
          <div className="sticky top-0 z-10 bg-background border-b">
            <div className="px-4 py-3 sm:px-8 sm:py-4 flex items-start justify-between gap-4">
              <div>
                <h1
                  id={titleId}
                  className="text-xl font-semibold tracking-tight"
                >
                  {title}
                </h1>
                {subtitle && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {subtitle}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {actions}
                {onClose && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Sticky close button */}
            {onClose && (
              <div className="sticky top-0 z-10 flex justify-end px-6 pt-4 pointer-events-none">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  aria-label="Close"
                  className="pointer-events-auto"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Scrollable header */}
            <div className="mx-auto max-w-4xl px-4 pb-6 pt-2 sm:px-8">
              {onClose && (
                <button
                  onClick={onClose}
                  className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back
                </button>
              )}
              <div className="flex items-start justify-between">
                <div>
                  <h1
                    id={titleId}
                    className="text-xl font-semibold tracking-tight"
                  >
                    {title}
                  </h1>
                  {subtitle && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {subtitle}
                    </p>
                  )}
                </div>
                {actions && (
                  <div className="flex items-center gap-2">{actions}</div>
                )}
              </div>
            </div>
          </>
        )}

        {children}
      </div>
    </div>
  );
}
